/**
 * File Name          : datasnap-mk1.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.0.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_BACKGROUNDTECH_LICENSE.txt', which is part of this source code package.
 */

import { AppResponsePacketPromise } from "../idl-physens.service";
import { AppResponsePacket } from "../models/packets/app-response-packet.model";
import { PhysensDevice } from "../models/physens-devices/physens-device.model";

enum APP_CMD_TYPE {
    GET_FILE_METADATA = 0x01,
    GET_RAW_FLASH_STATS = 0x02,
    LOG_BUFFER_POP = 0x03,
}


export class Datasnap_MK1 {

    static readonly APP_CMD_TYPE = APP_CMD_TYPE


    static payloadLengthWords(type: APP_CMD_TYPE): number {
        switch(type){
            case APP_CMD_TYPE.GET_FILE_METADATA: return 1;
            case APP_CMD_TYPE.GET_RAW_FLASH_STATS: return 1;
            case APP_CMD_TYPE.LOG_BUFFER_POP: return 0;
            default: return 0;
        }
    }


    static getFileMetadata(device: PhysensDevice | undefined, driveIdx: number, path: string): AppResponsePacketPromise | undefined {
        let pkt = device?.getAppCommandPacket()
    
        if(pkt!=undefined) {
        
            pkt.mPayloadLength = Datasnap_MK1.payloadLengthWords(Datasnap_MK1.APP_CMD_TYPE.GET_FILE_METADATA) + Math.ceil(path.length / 4);
            pkt.mPacketSubType = Datasnap_MK1.APP_CMD_TYPE.GET_FILE_METADATA
        
            pkt.payloadByteCalculator = (bytes: Uint8Array)=>{
                bytes[8] = driveIdx;
                const encoder = new TextEncoder();
                const pathStrBytes = encoder.encode(path)

                bytes.set(pathStrBytes, 9)
                return bytes
            }
            pkt.calcBytes()

            console.log("Sending get file metadata command with packet number: ", pkt.mPacketNumber)
            console.log("Packet bytes:", Array.from(pkt.mBytes!).map(b => b.toString(16).padStart(2, '0')).join(' ').toUpperCase())
        
            return device?.sendAppCommandPacket(pkt)
    
        }
        return undefined;
    }
    
    static fileMetadataFromAppResponsePacket(pkt: AppResponsePacket): FileMetadata | undefined {
        try {
            if(pkt.mPktSubType != Datasnap_MK1.APP_CMD_TYPE.GET_FILE_METADATA) {
                return undefined;
            }

            const dv = new DataView(pkt.mBytes!.buffer)
            const driveIdx = pkt.mBytes![8];
            const storedHash = dv.getUint32(9, true);
            const fileSize = dv.getUint32(13, true);
            const openDatetime = {
                frac256: dv.getUint8(17),
                sec: dv.getUint8(18),
                min: dv.getUint8(19),
                hour: dv.getUint8(20),
                day: dv.getUint8(21),
                month: dv.getUint8(22),
                year: 2000+dv.getUint8(23),
                weekDay: dv.getUint8(24)
            }
            const closeDatetime = {
                frac256: dv.getUint8(25),
                sec: dv.getUint8(26),
                min: dv.getUint8(27),
                hour: dv.getUint8(28),
                day: dv.getUint8(29),
                month: dv.getUint8(30),
                year: 2000+dv.getUint8(31),
                weekDay: dv.getUint8(32)
            }

            const writeStats: MetadataWriteStats = {
                total_write_calls: dv.getUint32(33, true),
                total_bytes_requested: dv.getUint32(37, true),
                flash_write_count: dv.getUint32(41, true),
                flash_bytes_written: dv.getUint32(45, true),
                first_error_write_num: dv.getUint16(49, true),
                first_error_code: dv.getInt16(51, true),
                first_error_offset: dv.getUint32(53, true),
                first_error_marker_us: dv.getUint32(57, true),
                first_error_retries: dv.getInt8(61),
                last_error_write_num: dv.getUint16(62, true),
                last_error_code: dv.getInt16(64, true),
                last_error_offset: dv.getUint32(66, true),
                last_error_marker_us: dv.getUint32(70, true),
                last_error_retries: dv.getInt8(74),
                total_errors: dv.getUint16(75, true),
                total_permanent_failures: dv.getUint16(77, true),
                crc_at_last_flush: dv.getUint16(79, true),
                tail_bytes_at_finalize: dv.getUint8(81)

            }
            

            return {
                driveIdx,
                storedHash,
                fileSize,
                openDatetime: `${openDatetime.year}-${String(openDatetime.month).padStart(2, '0')}-${String(openDatetime.day).padStart(2, '0')} ${String(openDatetime.hour).padStart(2, '0')}:${String(openDatetime.min).padStart(2, '0')}:${String(openDatetime.sec).padStart(2, '0')}`,
                closeDatetime: `${closeDatetime.year}-${String(closeDatetime.month).padStart(2, '0')}-${String(closeDatetime.day).padStart(2, '0')} ${String(closeDatetime.hour).padStart(2, '0')}:${String(closeDatetime.min).padStart(2, '0')}:${String(closeDatetime.sec).padStart(2, '0')}`,
                writeStats
            };
        } catch (error) {
            console.warn("Error parsing file metadata from app response packet: ", error)
            return undefined;
        }


        
    }

    static getRawFlashStats(device: PhysensDevice | undefined, driveIdx: number): AppResponsePacketPromise | undefined {
        let pkt = device?.getAppCommandPacket()
    
        if(pkt!=undefined) {
        
            pkt.mPayloadLength = Datasnap_MK1.payloadLengthWords(Datasnap_MK1.APP_CMD_TYPE.GET_RAW_FLASH_STATS);
            pkt.mPacketSubType = Datasnap_MK1.APP_CMD_TYPE.GET_RAW_FLASH_STATS
        
            pkt.payloadByteCalculator = (bytes: Uint8Array)=>{
                bytes[8] = driveIdx;
                return bytes;
            }
            pkt.calcBytes()
        
            return device?.sendAppCommandPacket(pkt)
    
        }
        return undefined;
    }

    static rawFlashStatsFromAppResponsePacket(pkt: AppResponsePacket): RawFlashDriveStats | undefined {
        try {
            if(pkt.mPktSubType != Datasnap_MK1.APP_CMD_TYPE.GET_RAW_FLASH_STATS) {
                return undefined;
            }

            const dv = new DataView(pkt.mBytes!.buffer)


            return {
                driveIdx: dv.getUint8(8),
                phase1_crc_failures: dv.getUint32(9, true),
                phase2_crc_failures: dv.getUint32(13, true),
                invalid_magic_count: dv.getUint32(17, true),
                interrupted_erases: dv.getUint32(21, true),
                slot_erases: dv.getUint32(25, true),
                reformats: dv.getUint32(29, true),
                format_failures: dv.getUint32(33, true),
                corruption_repairs: dv.getUint32(37, true),
                corruption_repairs_completed: dv.getUint32(41, true),
                files_created: dv.getUint32(45, true),
                files_finalized: dv.getUint32(49, true),
                files_incomplete: dv.getUint32(53, true),
                files_man_deleted: dv.getUint32(57, true),
                files_auto_deleted: dv.getUint32(61, true),
                files_overwritten: dv.getUint32(65, true),
                files_with_write_errors: dv.getUint32(69, true),
                files_with_perm_failures: dv.getUint32(73, true),
                create_fail_no_slot: dv.getUint32(77, true),
                create_fail_duplicate: dv.getUint32(81, true),
                create_fail_phase1_io: dv.getUint32(85, true),
                files_crc_verified: dv.getUint32(89, true),
                files_with_data_crc_mismatch: dv.getUint32(93, true),
                largest_file_bytes: dv.getUint32(97, true),
                files_fully_read: dv.getUint32(101, true),
                files_partial_read: dv.getUint32(105, true)
            };
        } catch (error) {
            console.warn("Error parsing raw flash drive stats from app response packet: ", error);
            return undefined;
        }
    }

    static formatRawFlashDriveStats(stats: RawFlashDriveStats): string[] {
        return [
            `Drive Index: ${stats.driveIdx}`,
            `Phase 1 CRC Failures: ${stats.phase1_crc_failures}`,
            `Phase 2 CRC Failures: ${stats.phase2_crc_failures}`,
            `Invalid Magic Count: ${stats.invalid_magic_count}`,
            `Interrupted Erases: ${stats.interrupted_erases}`,
            `Slot Erases: ${stats.slot_erases}`,
            `Reformats: ${stats.reformats}`,
            `Format Failures: ${stats.format_failures}`,
            `Corruption Repairs: ${stats.corruption_repairs}`,
            `Corruption Repairs Completed: ${stats.corruption_repairs_completed}`,
            `Files Created: ${stats.files_created}`,
            `Files Finalized: ${stats.files_finalized}`,
            `Files Incomplete: ${stats.files_incomplete}`,
            `Files Manually Deleted: ${stats.files_man_deleted}`,
            `Files Automatically Deleted: ${stats.files_auto_deleted}`,
            `Files Overwritten: ${stats.files_overwritten}`,
            `Files with Write Errors: ${stats.files_with_write_errors}`,
            `Files with Permission Failures: ${stats.files_with_perm_failures}`,
            `Create Failures - No Slot: ${stats.create_fail_no_slot}`,
            `Create Failures - Duplicate: ${stats.create_fail_duplicate}`,
            `Create Failures - Phase 1 IO: ${stats.create_fail_phase1_io}`,
            `Files CRC Verified: ${stats.files_crc_verified}`,
            `Files with Data CRC Mismatch: ${stats.files_with_data_crc_mismatch}`,
            `Largest File Bytes: ${stats.largest_file_bytes}`,
            `Files Fully Read: ${stats.files_fully_read}`,
            `Files Partial Read: ${stats.files_partial_read}`
        ];

    }

    static getLogBufferPop(device: PhysensDevice | undefined): AppResponsePacketPromise | undefined {
        let pkt = device?.getAppCommandPacket();

        if(pkt!=undefined) {
        
            pkt.mPayloadLength = Datasnap_MK1.payloadLengthWords(Datasnap_MK1.APP_CMD_TYPE.LOG_BUFFER_POP);
            pkt.mPacketSubType = Datasnap_MK1.APP_CMD_TYPE.LOG_BUFFER_POP;
            pkt.payloadByteCalculator = (bytes: Uint8Array)=>{
                return bytes;
            }   
            pkt.calcBytes()
            return device?.sendAppCommandPacket(pkt);
        }
        return undefined;

    }

    static logBufferPopFromAppResponsePacket(pkt: AppResponsePacket): DebugLogCapture | undefined {
        if(pkt.mPktSubType != Datasnap_MK1.APP_CMD_TYPE.LOG_BUFFER_POP) {
            return undefined;
        }

        const dv = new DataView(pkt.mBytes!.buffer)

        return {
            bytes_available: dv.getUint16(8, true),
            entry_count: dv.getUint16(10, true),
            drop_count: dv.getUint32(12, true),
            bytes_popped: dv.getUint16(16, true),
            entry_string: new TextDecoder().decode(pkt.mBytes!.slice(18))
        };
    }

}

export interface FileMetadata {
    driveIdx: number;
    storedHash: number;
    fileSize: number;
    openDatetime: string;
    closeDatetime: string;
    writeStats: MetadataWriteStats;
}

export interface MetadataWriteStats {
    total_write_calls: number;
        total_bytes_requested: number;
        flash_write_count: number;
        flash_bytes_written: number;
        first_error_write_num: number;
        first_error_code: number;
        first_error_offset: number;
        first_error_marker_us: number;
        first_error_retries: number;
        last_error_write_num: number;
        last_error_code: number;
        last_error_offset: number;
        last_error_marker_us: number;
        last_error_retries: number;
        total_errors: number;
        total_permanent_failures: number;
        crc_at_last_flush: number;
        tail_bytes_at_finalize: number;
}

export interface RawFlashDriveStats {
    driveIdx: number;
    phase1_crc_failures: number;
    phase2_crc_failures: number;
    invalid_magic_count: number;
    interrupted_erases: number;
    slot_erases: number;
    reformats: number;
    format_failures: number;
    corruption_repairs: number;
    corruption_repairs_completed: number;
    files_created: number;
    files_finalized: number;
    files_incomplete: number;
    files_man_deleted: number;
    files_auto_deleted: number;
    files_overwritten: number;
    files_with_write_errors: number;
    files_with_perm_failures: number;
    create_fail_no_slot: number;
    create_fail_duplicate: number;
    create_fail_phase1_io: number;
    files_crc_verified: number;
    files_with_data_crc_mismatch: number;
    largest_file_bytes: number;
    files_fully_read: number;
    files_partial_read: number;
}

export interface DebugLogCapture {
    bytes_available: number;
    entry_count: number;
    drop_count: number;
    bytes_popped: number;
    entry_string: string;
}